/**
 * Тонкая обёртка над Telegram WebApp SDK.
 * Использует нативный window.Telegram.WebApp — никаких зависимостей от сторонних оберток.
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Any = any;

interface TGWebApp {
  initData: string;
  initDataUnsafe: {
    user?: { id: number; username?: string; first_name?: string };
    auth_date?: number;
    hash?: string;
  };
  colorScheme: 'light' | 'dark';
  themeParams: Record<string, string>;
  expand(): void;
  ready(): void;
  close(): void;
  MainButton: Any;
  BackButton: Any;
  HapticFeedback: {
    impactOccurred(style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft'): void;
    notificationOccurred(type: 'error' | 'success' | 'warning'): void;
    selectionChanged(): void;
  };
  openTelegramLink(url: string): void;
  showAlert(message: string, cb?: () => void): void;
  showConfirm(message: string, cb: (ok: boolean) => void): void;
  onEvent(event: string, cb: () => void): void;
  offEvent(event: string, cb: () => void): void;
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TGWebApp };
  }
}

let webApp: TGWebApp | null = null;

export function initTelegram() {
  webApp = window.Telegram?.WebApp ?? null;
  if (!webApp) {
    console.warn('⚠️ Telegram WebApp not available — running in standalone mode');
    return;
  }
  webApp.ready();
  webApp.expand();

  // Применяем theme
  const root = document.documentElement;
  if (webApp.colorScheme === 'dark') root.classList.add('dark');

  // Фирменная оранжевая тема для кнопок (переопределяем Telegram default)
  for (const [k, v] of Object.entries(webApp.themeParams)) {
    root.style.setProperty(`--tg-theme-${k.replace(/_/g, '-')}`, v);
  }
}

export function tg(): TGWebApp | null {
  return webApp;
}

export function haptic(style: 'light' | 'medium' | 'heavy' = 'medium') {
  webApp?.HapticFeedback.impactOccurred(style);
}

export function notifyOk() {
  webApp?.HapticFeedback.notificationOccurred('success');
}
export function notifyErr() {
  webApp?.HapticFeedback.notificationOccurred('error');
}

export function closeApp() {
  webApp?.close();
}

export function getInitData(): string {
  return webApp?.initData ?? '';
}

/**
 * true, если приложение открыто как обычный сайт (не внутри Telegram).
 *
 * index.html грузит telegram-web-app.js безусловно, поэтому
 * window.Telegram.WebApp определён даже в обычном браузере — просто с
 * пустой initData. Единственный надёжный сигнал «мы реально внутри
 * Telegram» — непустая initData (Telegram подставляет её сам).
 */
export function isStandalone(): boolean {
  const wa = window.Telegram?.WebApp;
  return !wa || !wa.initData || wa.initData.length === 0;
}

/** showAlert Telegram → браузерный фолбэк в standalone. */
export function showAlertFallback(message: string): void {
  if (webApp) webApp.showAlert(message);
  else window.alert(message);
}
